import { useContext, useMemo } from 'react';

import AccountContext from 'contexts/AccountContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import { GroupsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GroupsProvider';

export interface InsightsChatBudget {
  // Whether the user may use the chat at all (drives icon visibility).
  isEligible: boolean;
  // The user's monthly budget in USD, or null for unlimited (staff). Only
  // meaningful when isEligible is true.
  budgetUsd: number | null;
}

// Insights (the chat, Studio's Build with AI, Explain this value) is
// temporarily off for everyone, staff included: no one is eligible, so none of
// its entry points show. Set to true to bring it back.
export const IS_INSIGHTS_ENABLED = false;

// Resolve a user's effective monthly AI budget (pure, unit-tested):
//  - a per-user override always wins;
//  - otherwise the HIGHEST budget among the AI-enabled groups they're in;
//  - otherwise staff get unlimited;
//  - otherwise they're not eligible (no chat icon).
export const resolveEffectiveBudget = ({
  perUserOverrideUsd,
  groupBudgetsUsd,
  isStaff,
}: {
  perUserOverrideUsd: number | undefined;
  groupBudgetsUsd: number[];
  isStaff: boolean;
}): InsightsChatBudget => {
  if (typeof perUserOverrideUsd === 'number') {
    return { isEligible: true, budgetUsd: perUserOverrideUsd };
  }
  if (groupBudgetsUsd.length > 0) {
    return { isEligible: true, budgetUsd: Math.max(...groupBudgetsUsd) };
  }
  if (isStaff) {
    return { isEligible: true, budgetUsd: null };
  }
  return { isEligible: false, budgetUsd: null };
};

// Effective AI chat budget for the current user, from their group memberships +
// any per-user override + staff status. The account-level feature gate is a
// separate, prior check (FeatureGate).
const useInsightsChatBudget = (): InsightsChatBudget => {
  const { id: userId, isFleetOpsStaff } = useContext(CurrentUserContext);
  const { groupsCurrentUserIsIn } = useContext(GroupsContext);
  const { aiBudgets } = useContext(AccountContext);

  return useMemo(() => {
    if (!IS_INSIGHTS_ENABLED) {
      return { isEligible: false, budgetUsd: null };
    }
    const groupBudgetsUsd = groupsCurrentUserIsIn
      .map((group) => group.aiBudgetUsd)
      .filter((budget): budget is number => typeof budget === 'number');
    const perUserOverrideUsd = aiBudgets?.perUserOverrideUsd?.[userId];
    return resolveEffectiveBudget({
      perUserOverrideUsd,
      groupBudgetsUsd,
      isStaff: !!isFleetOpsStaff,
    });
  }, [groupsCurrentUserIsIn, aiBudgets, userId, isFleetOpsStaff]);
};

export default useInsightsChatBudget;
